// Sarvam voice call: recording player, call summary and transcript (same shape as the reference
// product's voice recording modal; the call itself runs on Sarvam, this app only stores the result).
import React, { useEffect, useRef, useState } from 'react';
import { FaPause, FaPlay, FaRobot, FaUser } from 'react-icons/fa';
import { SALON_NAME, backendUrl, formatDateTime, formatPhone } from '../services/api';
import { DetailRow, Modal } from './ui';

function AudioPlayer({ src }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setFailed(false);
  }, [src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => setFailed(true));
    else el.pause();
  };

  const seek = (e) => {
    const el = ref.current;
    if (!el || !el.duration) return;
    el.currentTime = (Number(e.target.value) / 100) * el.duration;
  };

  return (
    <div>
      <div className="nye-audio">
        <audio
          ref={ref}
          src={src}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setProgress(0);
          }}
          onError={() => setFailed(true)}
          onTimeUpdate={() => {
            const el = ref.current;
            if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
          }}
        />
        <button type="button" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? <FaPause size={12} /> : <FaPlay size={12} style={{ marginLeft: 2 }} />}
        </button>
        <input type="range" min={0} max={100} step={0.1} value={progress} onChange={seek} aria-label="Seek" />
      </div>
      {failed ? (
        <div className="nye-small nye-muted nye-mt-8">
          The recording could not be played here.{' '}
          <a href={src} target="_blank" rel="noopener noreferrer" className="nye-purple-text" style={{ textDecoration: 'underline' }}>
            Open the file
          </a>
        </div>
      ) : null}
    </div>
  );
}

const END_REASONS = {
  AGENT_ENDS: 'Agent ended the call',
  agent_request: 'Agent ended the call',
  USER_ENDS: 'Caller hung up',
  user_hangup: 'Caller hung up',
};

export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '';
  const s = Math.round(Number(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function endReasonLabel(reason) {
  if (!reason) return '';
  return END_REASONS[reason] || reason.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

// The call as Sarvam heard it: the caller's own words (Tamil, Hindi or English), with the English
// line under it when the call was in another language.
function Transcript({ lines, name }) {
  return (
    <div className="nye-chat-body" style={{ maxHeight: '44vh' }}>
      {lines.map((line, i) =>
        line.role === 'caller' ? (
          <div key={i} className="nye-msg nye-msg--in">
            <div className="nye-msg-inner">
              <div className="nye-msg-bubble">
                {line.text}
                {line.text_en ? <div className="nye-msg-meta">{line.text_en}</div> : null}
              </div>
              <div className="nye-chat-avatar nye-chat-avatar--customer" title={name || 'Caller'}>
                <FaUser size={13} />
              </div>
            </div>
          </div>
        ) : (
          <div key={i} className="nye-msg nye-msg--out">
            <div className="nye-msg-inner">
              <div className="nye-chat-avatar nye-chat-avatar--bot" title={`${SALON_NAME} voice agent`}>
                <FaRobot size={15} />
              </div>
              <div className="nye-msg-bubble">
                {line.text}
                {line.text_en ? <div className="nye-msg-meta">{line.text_en}</div> : null}
              </div>
            </div>
          </div>
        ),
      )}
    </div>
  );
}

export default function RecordingModal({ call, onClose }) {
  if (!call) return null;
  const lines = call.transcript || [];
  return (
    <Modal title="Voice call" onClose={onClose} size="wide">
      <DetailRow label="Customer">{call.customer_name}</DetailRow>
      <DetailRow label="Phone">{formatPhone(call.customer_phone)}</DetailRow>
      <DetailRow label="Call time">{formatDateTime(call.call_at || call.created_at)}</DetailRow>
      {call.outcome ? (
        <DetailRow label="Outcome">
          {call.outcome === 'booked' ? 'Booked' : call.outcome === 'callback' ? 'Teammate callback' : 'Enquiry'}
          {call.outcome === 'callback' && call.callback_reason ? ` · ${call.callback_reason}` : ''}
        </DetailRow>
      ) : null}
      {(call.bookings && call.bookings.length
        ? call.bookings
        : call.service_name
          ? [{ id: 'one', service_name: call.service_name, starts_at: call.starts_at, staff_name: call.staff_name }]
          : []
      ).map((b) => (
        <DetailRow key={b.id} label="Booked">
          {b.service_name}
          {b.starts_at ? ` · ${formatDateTime(b.starts_at)}` : ''} · {b.staff_name || 'Any available stylist'}
          {b.booking_name && b.booking_name !== call.customer_name ? ` · for ${b.booking_name}` : ''}
        </DetailRow>
      ))}
      {call.duration_seconds !== null && call.duration_seconds !== undefined ? (
        <DetailRow label="Duration">{formatDuration(call.duration_seconds)}</DetailRow>
      ) : null}
      {call.language ? <DetailRow label="Language">{call.language}</DetailRow> : null}
      {call.end_reason ? <DetailRow label="Ended">{endReasonLabel(call.end_reason)}</DetailRow> : null}
      {call.sarvam_call_id ? (
        <DetailRow label="Call id">
          <span className="nye-mono">{call.sarvam_call_id}</span>
        </DetailRow>
      ) : null}
      <div className="nye-section-label nye-mt-16">Recording</div>
      {call.recording_url ? (
        <AudioPlayer src={backendUrl(call.recording_url)} />
      ) : (
        <div className="nye-callout">No recording was sent for this call.</div>
      )}
      <div className="nye-section-label nye-mt-16">Call summary</div>
      <div className="nye-pre">{call.summary || 'No summary was sent for this call.'}</div>
      <div className="nye-section-label nye-mt-16">Transcript</div>
      {lines.length ? (
        <Transcript lines={lines} name={call.customer_name} />
      ) : (
        <div className="nye-callout">No transcript was sent for this call.</div>
      )}
    </Modal>
  );
}
